import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Game } from './game/Game';
import type { UnitOrder } from './game/Entity';
import { Hud } from './hud/Hud';
import { useStore } from './state/store';

declare global {
  interface Window {
    /** Development-only bridge used by the focused browser release smoke suite. */
    __STEEL_TEST__?: {
      snapshot: () => unknown;
      destroyTrainingTarget: () => boolean;
      restartCampaign: () => void;
    };
  }
}

const app = document.getElementById('app');
if (!app) throw new Error('No #app container');

const canvas = document.createElement('canvas');
canvas.id = 'scene';
app.appendChild(canvas);

const hudHost = document.createElement('div');
hudHost.id = 'hud';
hudHost.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:10;';
app.appendChild(hudHost);

const game = new Game(canvas);
game.start();

// Launching a run always rebuilds the player so it picks up the currently-selected
// plane + perks from the Hangar. Used by splash-tap, gamepad start, hangar LAUNCH,
// and game-over RETRY. A normal launch clears any tutorial flag.
const launch = () => { useStore.getState().setTutorial(false); game.restart(); };
// Start the authored no-fail training sortie. Flag must be set BEFORE restart so the scripted
// contacts and staged objective card initialize with the new run.
const startTutorial = () => { useStore.getState().setTutorial(true); game.restart(); };

// A deliberately tiny test bridge: it exists only in Vite development mode and only when the
// explicit ?e2e=1 flag is present. Production builds tree-shake the branch completely.
if (import.meta.env.DEV && new URLSearchParams(location.search).has('e2e')) {
  window.__STEEL_TEST__ = {
    snapshot: () => {
      const st = useStore.getState();
      return {
        phase: st.phase,
        tutorial: st.tutorial,
        tutorialDone: { ...st.tutorialDone },
        wave: st.wave,
        ai: st.ai,
        fxLite: st.fxLite,
        colorblind: st.colorblind,
        reducedMotion: st.reducedMotion,
        mapOpen: st.mapOpen,
        controlledId: st.controlledId,
        controlUnits: st.controlUnits.map((u) => ({ ...u })),
        trainingTargets: game.entities
          .filter((e) => e.trainingTarget && !e.dead && e.alive())
          .map((e) => ({ id: e.id, kind: e.kind })),
      };
    },
    destroyTrainingTarget: () => {
      const target = game.entities.find((e) => e.trainingTarget && !e.dead && e.alive());
      if (!target) return false;
      target.dead = true;
      return true;
    },
    restartCampaign: launch,
  };
}

// Tap the canvas to launch from the splash (the tutorial overlay).
canvas.addEventListener('pointerdown', () => {
  if (useStore.getState().phase === 'splash') launch();
}, { once: false });

// PWA: cache the game for offline play (production only — a SW caching dev-server modules is
// debugging hell). './sw.js' resolves under the deploy base path (/SteelTempest/), scope ./.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').catch((e) => console.warn('[sw] register failed', e));
  });
}

// Keyboard: Enter/Space launches from splash or game-over (desktop parity with tap / gamepad A).
window.addEventListener('keydown', (e) => {
  if (e.code !== 'Enter' && e.code !== 'Space') return;
  const phase = useStore.getState().phase;
  if (phase === 'splash' || phase === 'gameover' || phase === 'victory') launch();
});

// Gamepad: a face button launches from splash or game-over.
setInterval(() => {
  const st = useStore.getState();
  if (!game.input.consumeGamepadStart()) return;
  if (st.phase === 'splash' || st.phase === 'gameover') launch();
}, 80);

// If the HUD ever throws, show a reload fallback instead of a dead white overlay (the 3D
// game keeps rendering underneath; only the React layer is replaced).
class HudErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err: unknown) { console.error('[hud] crashed', err); }
  render() {
    if (!this.state.failed) return this.props.children;
    return React.createElement('div', {
      style: {
        position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)',
        background: 'rgba(0,0,0,0.85)', color: '#fff', padding: '16px 22px', borderRadius: 8,
        font: '700 14px ui-monospace,monospace', letterSpacing: 1, textAlign: 'center',
        pointerEvents: 'auto', zIndex: 99,
      },
    },
      'HUD ERROR',
      React.createElement('button', {
        onClick: () => location.reload(),
        style: {
          display: 'block', margin: '12px auto 0', padding: '8px 18px', cursor: 'pointer',
          background: '#2e6da4', color: '#fff', border: 'none', borderRadius: 6,
          font: 'inherit', letterSpacing: 2,
        },
      }, 'RELOAD'),
    );
  }
}

createRoot(hudHost).render(React.createElement(HudErrorBoundary, null, React.createElement(Hud, {
  onRestart: launch,
  onTutorial: startTutorial,
  onJump: (dir: 1 | -1) => game.jumpCycle(dir),
  onSelectUnit: (id: number) => {
    const unit = game.entities.find((e) => e.id === id);
    if (unit) game.takeControl(unit);
  },
  onJumpCrisis: () => game.jumpToCrisis(),
  onToggleMap: () => game.toggleMap(),
  onIssueOrder: (unitId: number, order: UnitOrder) => game.issueOrder(unitId, order),
})));
